/**
 * @orgazone/core-vat — moteur TVA multi-taux BIDIRECTIONNEL (SPEC §24.6 + §30.2/§30.3, matrice §24.6bis D).
 * DEUX sens de calcul + le régime de la marge, UN SEUL arrondi commercial half-up chacun (§30.3), AGRÉGÉ
 * PAR TAUX (jamais ligne par ligne) → HT + TVA = TTC garanti au centime, sans accumulation d'erreur :
 *
 *  • B2C / CAISSE / FISCAL — le prix affiché INCLUT la taxe (le TTC est la grandeur SIGNÉE NF525) :
 *      HT_taux  = arrondi(TTC_taux × 10000/(10000+pb))   ; TVA_taux = TTC_taux − HT_taux  (par soustraction).
 *  • B2B / ERP — le prix est saisi HORS taxe, la TVA est AJOUTÉE à la facture :
 *      TVA_taux = arrondi(HT_taux × pb/10000)            ; TTC_taux = HT_taux + TVA_taux   (par addition).
 *  • TVA SUR MARGE (occasion / art / antiquités, art. 297 A CGI) : la TVA porte sur la MARGE du revendeur
 *      (PV − PA), pas sur le prix total ; marge ≤ 0 → TVA nulle. (Aussi la base arithmétique de la « sales
 *      tax » US ajoutée par-dessus un prix hors taxe : c'est le sens B2B.)
 *
 * Le CONTEXTE client (B2C / B2B / marge) choisit le sens. ⚠️ Les sens TTC→HT et HT→TTC ne sont PAS bijectifs
 * au centime (points de départ différents : étiquette TTC vs étiquette HT) — c'est NORMAL, on ne fait jamais
 * l'aller-retour. ARGENT = CENTIMES ENTIERS ; taux en POINTS DE BASE (20 % = 2000) → arithmétique 100 %
 * entière (via @orgazone/core-money). PAYS-AGNOSTIQUE : `VAT_RATES_BP` = raccourcis FR de confort, mais tout
 * `rateBp` est accepté (Italie 22/10/5/4 % → 2200/1000/500/400 ; autres pays = DONNÉE du référentiel
 * article/BO, jamais codée en dur). Fonctions pures, zéro dépendance externe — partagé caisse / ERP / serveur
 * / export compta (« à appliquer à l'identique en UI, backend et export compta », §30.3).
 */
import { divRoundHalfUp, mulDivRound } from '@orgazone/core-money';
export { divRoundHalfUp }; // ré-export : SOURCE UNIQUE de l'arrondi = @orgazone/core-money (dé-dup §24.6bis A)

/** Référentiel de taux FRANÇAIS courants (§30.2), en points de base. Confort seulement — tout `rateBp` reste
 *  accepté par le moteur (autres pays = donnée : Italie 22/10/5/4 % → 2200/1000/500/400). */
export const VAT_RATES_BP = {
  NORMAL: 2000, // 20 %
  INTERMEDIAIRE: 1000, // 10 %
  REDUIT: 550, // 5,5 %
  PARTICULIER: 210, // 2,1 %
  /** 0 % — titres (chèque cadeau / avoir / acompte, §30.2), export, exonération, autoliquidation intra-UE. */
  ZERO: 0,
} as const;

/**
 * FORME HUMAINE D'UN TAUX — 550 → « 5,5 % », 2000 → « 20 % ».
 *
 * ⚠️ EXTRAITE À SA DEUXIÈME COPIE (règle de 2, §24.3) : l'écran des clôtures la portait, le plan
 * comptable en avait besoin à son tour. Recopiée, elle aurait fini par afficher « 5.5 % » d'un côté et
 * « 5,5 % » de l'autre pour la même colonne de la même base.
 *
 * ⚠️ POURQUOI ICI ET NON DANS UN SATELLITE DE PRÉSENTATION, contrairement aux montants (`money-format`
 * face à `core-money`) : le rendu d'un MONTANT dépend d'une devise, d'une locale et d'un séparateur de
 * milliers — autant de décisions étrangères au calcul. Le rendu d'un TAUX n'est qu'une division par
 * cent : c'est de l'arithmétique sur des points de base, la matière même de ce cœur. `defaultVatCode`,
 * juste à côté, nomme déjà un taux — celle-ci ne fait pas autre chose.
 *
 * ⚠️ VIRGULE DÉCIMALE et espace insécable avant le signe : c'est la convention typographique
 * française, et un taux fiscal se relit dans des documents qui la suivent.
 */
export function formatRateBp(rateBp: number, withSign = true): string {
  const pct = rateBp / 100;
  const nombre = Number.isInteger(pct) ? String(pct) : pct.toFixed(2).replace(/0$/, '').replace('.', ',');
  // ⚠️ ÉCHAPPEMENT ET NON CARACTÈRE BRUT : une espace insécable posée telle quelle est invisible à la
  //    relecture, et le premier éditeur qui « nettoie » les espaces la retransforme en espace ordinaire.
  // ⚠️ SANS LE SIGNE pour amorcer un CHAMP de saisie, qui porte déjà « % » à côté : relu par
  //    `parseRateBp`, le texte redonne exactement le même taux.
  return withSign ? `${nombre}\u00A0%` : nombre;
}

/**
 * LIT UN TAUX SAISI EN POUR CENT (« 10 », « 12,5 », « 12.25 % ») — en points de base entiers, `null`
 * s'il n'en est pas un.
 *
 * ⚠️ REFUSER, JAMAIS CORRIGER : « 12,555 » ou « 101 » ne deviennent ni 12,56 ni 100 — un taux arrondi
 *    ou borné en silence n'est plus celui qu'on a tapé, et une remise s'appliquerait sur un nombre que
 *    personne n'a choisi. Deux décimales au plus, de 0 à 100 %.
 * ⚠️ VIDE VAUT ZÉRO : un champ effacé dit « aucun taux ». Lu en entiers, jamais par `parseFloat`
 *    (qui accepte « 12.5.3 » et produirait 1249,9999… points de base).
 */
export function parseRateBp(saisie: string): number | null {
  const s = saisie.trim().replace(/\s*%$/, '').replace(',', '.');
  if (s === '') return 0;
  const m = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) return null;
  const bp = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
  return bp <= 10_000 ? bp : null;
}

/** Convertit un taux exprimé en pourcentage (20, 5.5, 2.1, 22…) en points de base entiers. */
export function rateToBasisPoints(ratePercent: number): number {
  const bp = Math.round(ratePercent * 100);
  if (!Number.isFinite(bp) || bp < 0 || bp > 10000) throw new Error(`core-vat: taux invalide (${ratePercent} %)`);
  return bp;
}

// Garde commune d'un taux en points de base : entier ≥ 0 (pas de plafond — cohérent avec l'usage multi-pays).
function assertRateBp(rateBp: number): void {
  if (!Number.isInteger(rateBp) || rateBp < 0) throw new Error(`core-vat: taux en points de base entier ≥ 0 attendu (${rateBp})`);
}

/** Bloc de ventilation par taux (résultat) — un bloc par taux présent (§30.3 multi-taux). Forme COMMUNE aux 2 sens. */
export interface VatBlock {
  rateBp: number;
  ttcCents: number;
  htCents: number;
  vatCents: number;
  /**
   * CODE TVA du dictionnaire (R13 `ENC-TIK-TVA-NID`), DISTINCT du taux et obligatoire.
   * Absent ⇒ code déduit du taux par `defaultVatCode`. À RENSEIGNER explicitement dès qu'un régime
   * particulier coexiste avec le régime normal au même taux (TVA sur la marge), sans quoi les deux
   * fusionnent dans la pièce scellée et dans les grands totaux — fusion IRRÉVERSIBLE une fois cumulée.
   */
  code?: string;
}

export interface VatBreakdown {
  /** Blocs triés par taux CROISSANT (ordre canonique — sérialisation fiscale stable). */
  blocks: VatBlock[];
  totalTtcCents: number;
  totalHtCents: number;
  totalVatCents: number;
}

// Totaux d'une ventilation (commun aux deux sens — DRY).
function totals(blocks: VatBlock[]): VatBreakdown {
  let totalTtcCents = 0;
  let totalHtCents = 0;
  let totalVatCents = 0;
  for (const b of blocks) {
    totalTtcCents += b.ttcCents;
    totalHtCents += b.htCents;
    totalVatCents += b.vatCents;
  }
  return { blocks, totalTtcCents, totalHtCents, totalVatCents };
}

// ============================================================================
// SENS 1 — B2C / CAISSE / FISCAL : le prix INCLUT la taxe → on DÉRIVE le HT depuis le TTC.
// ============================================================================

/** Une entrée de ventilation TTC : montant TTC (centimes, signé — retours négatifs) + taux (pb). */
export interface VatEntry {
  ttcCents: number;
  rateBp: number;
}

/** HT depuis un TTC pour un taux donné : arrondi_centime(ttc × 10000 / (10000 + pb)). */
export function htFromTtc(ttcCents: number, rateBp: number): number {
  if (!Number.isSafeInteger(ttcCents)) throw new Error(`core-vat: TTC en centimes entiers sûrs attendu (${ttcCents})`);
  assertRateBp(rateBp);
  if (rateBp === 0) return ttcCents;
  // Produit ttc × 10000 en BigInt via mulDivRound (jamais de débordement) — doctrine §24.6bis A.
  return mulDivRound(ttcCents, 10000, 10000 + rateBp);
}

/**
 * Ventilation AGRÉGÉE par taux depuis des TTC (méthode de référence §30.3, sens B2C/fiscal).
 * Les remises sont appliquées sur le TTC EN AMONT (entrées = TTC nets payés) ; négatifs (retours) supportés.
 */
export function ventilate(entries: VatEntry[]): VatBreakdown {
  const byRate = new Map<number, number>();
  for (const e of entries) {
    if (!Number.isSafeInteger(e.ttcCents)) throw new Error('core-vat: TTC en centimes entiers sûrs attendu');
    assertRateBp(e.rateBp);
    byRate.set(e.rateBp, (byRate.get(e.rateBp) ?? 0) + e.ttcCents);
  }
  const blocks: VatBlock[] = [...byRate.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rateBp, ttcCents]) => {
      const htCents = htFromTtc(ttcCents, rateBp);
      return { rateBp, ttcCents, htCents, vatCents: ttcCents - htCents };
    });
  return totals(blocks);
}

// ============================================================================
// SENS 2 — B2B / ERP : le prix est HORS taxe → on AJOUTE la TVA au HT.
// (Aussi la base de la « sales tax » US ajoutée par-dessus un prix hors taxe.)
// ============================================================================

/** Une entrée de ventilation HT : montant HT (centimes, signé) + taux (pb). */
export interface HtEntry {
  htCents: number;
  rateBp: number;
}

/** TVA due depuis un HT pour un taux donné : arrondi_centime(ht × pb / 10000). Base B2B/ERP + taxe « par-dessus ». */
export function vatFromHt(htCents: number, rateBp: number): number {
  if (!Number.isSafeInteger(htCents)) throw new Error(`core-vat: HT en centimes entiers sûrs attendu (${htCents})`);
  assertRateBp(rateBp);
  if (rateBp === 0) return 0;
  return mulDivRound(htCents, rateBp, 10000); // BigInt interne, half-up §30.3
}

/** TTC depuis un HT : HT + TVA (la TVA est l'arrondi, le TTC par addition → HT + TVA = TTC exact). */
export function ttcFromHt(htCents: number, rateBp: number): number {
  return htCents + vatFromHt(htCents, rateBp);
}

/**
 * Ventilation AGRÉGÉE par taux depuis des HT (sens B2B/ERP, §30.3 inversé) :
 * HT_taux = Σ HT du taux (exact) → TVA_taux = arrondi(HT_taux × pb) → TTC_taux = HT_taux + TVA_taux.
 */
export function ventilateFromHt(entries: HtEntry[]): VatBreakdown {
  const byRate = new Map<number, number>();
  for (const e of entries) {
    if (!Number.isSafeInteger(e.htCents)) throw new Error('core-vat: HT en centimes entiers sûrs attendu');
    assertRateBp(e.rateBp);
    byRate.set(e.rateBp, (byRate.get(e.rateBp) ?? 0) + e.htCents);
  }
  const blocks: VatBlock[] = [...byRate.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([rateBp, htCents]) => {
      const vatCents = vatFromHt(htCents, rateBp);
      return { rateBp, htCents, vatCents, ttcCents: htCents + vatCents };
    });
  return totals(blocks);
}

// ============================================================================
// SENS 3 — TVA SUR MARGE (occasion / art / antiquités, art. 297 A CGI).
// ============================================================================

/** Résultat d'un calcul de TVA sur marge. */
export interface MarginVat {
  /** Marge = PV TTC − PA TTC (peut être négative → vente à perte). */
  marginCents: number;
  /** TVA due sur la marge (0 si marge ≤ 0). */
  vatCents: number;
  /** Base HT de la marge = marge − TVA (0 si marge ≤ 0). */
  taxableBaseCents: number;
}

/**
 * TVA sur la MARGE du revendeur (biens d'occasion, art, antiquités). La TVA porte sur (PV − PA), PAS sur le
 * prix total ; la marge est traitée comme TTC (elle inclut la TVA) ; marge ≤ 0 → TVA nulle (jamais de crédit
 * de TVA sur une vente à perte). ⚠️ Une SEULE fonction couvre le « coup par coup » (par article) ET la
 * « globalisation » (passer les TOTAUX période de ventes et d'achats). Quel article relève de ce régime =
 * CONFIG (core-pricing/BO), jamais recalculé ailleurs (décision CŒUR·05). La facture ne détaille pas la TVA
 * (mention « régime particulier – biens d'occasion ») — c'est un affichage produit, pas le calcul.
 */
export function marginVat(salePriceTtcCents: number, purchasePriceTtcCents: number, rateBp: number): MarginVat {
  if (!Number.isSafeInteger(salePriceTtcCents) || !Number.isSafeInteger(purchasePriceTtcCents)) {
    throw new Error("core-vat: prix de vente et d'achat en centimes entiers sûrs attendus");
  }
  assertRateBp(rateBp);
  const marginCents = salePriceTtcCents - purchasePriceTtcCents;
  if (marginCents <= 0) return { marginCents, vatCents: 0, taxableBaseCents: 0 };
  const taxableBaseCents = htFromTtc(marginCents, rateBp); // la marge est TTC → base HT dérivée (même arrondi §30.3)
  return { marginCents, vatCents: marginCents - taxableBaseCents, taxableBaseCents };
}

/**
 * CODE TVA au sens des dictionnaires (R13 `ENC-TIK-TVA-NID` / `ENC-TIK-LIG-TAX-NID`) — donnée
 * OBLIGATOIRE, DISTINCTE du taux, exigée en base, en archive ET à l'impression.
 * Pourquoi un code séparé alors qu'on a déjà le taux : deux ventes au même taux peuvent relever de
 * RÉGIMES différents (régime normal vs TVA sur la marge, art. 297 A CGI) ; sans code, elles sont
 * indistinguables dans la pièce scellée et dans les grands totaux, où la fusion est irréversible.
 * Le code par défaut suit la convention française A=20 % · B=10 % · C=5,5 % · D=2,1 % · E=0 % ;
 * un régime particulier fournit le SIEN via `VatBlock.code`.
 */
const VAT_CODE_BY_RATE: Readonly<Record<number, string>> = { 2000: 'A', 1000: 'B', 550: 'C', 210: 'D', 0: 'E' };

/** Code TVA par défaut d'un taux. `X` = taux hors barème courant (jamais silencieux : visible à l'audit). */
export function defaultVatCode(rateBp: number): string {
  return VAT_CODE_BY_RATE[rateBp] ?? 'X';
}

/**
 * LE RÉGIME DE TVA DE L'ASSUJETTI — une propriété de la SOCIÉTÉ, jamais d'un article.
 *
 * · `normal`    — la TVA est collectée au taux de chaque article ;
 * · `franchise` — FRANCHISE EN BASE (art. 293 B du CGI) : l'entreprise ne facture la TVA sur rien, toutes ses
 *                 ventes partent à 0 % et chaque pièce porte la mention légale.
 * ⚠️ UN ARTICLE À 0 % N'EST PAS UN MAGASIN EN FRANCHISE : l'un est exonéré (un chèque cadeau, une opération
 *    hors champ), l'autre ne facture la TVA sur aucune vente. Confondus, la mention s'imprimerait sur le ticket
 *    d'un assujetti, ou la TVA serait collectée chez qui n'a pas le droit de la facturer.
 */
export type VatRegime = 'normal' | 'franchise';

/**
 * LE CODE TVA DE LA FRANCHISE EN BASE — distinct de « E », l'exonération d'un article.
 * ⚠️ C'EST LUI QUI PORTE LE RÉGIME JUSQU'À L'AUDITEUR : il entre dans la ventilation SIGNÉE (`formatVentilation`),
 *    sur chaque ligne et chaque bloc scellés, dans l'archive. Un duplicata imprimé des mois plus tard retrouve la
 *    mention dans la pièce, jamais dans la configuration du jour — le magasin a pu sortir de la franchise depuis.
 */
export const VAT_CODE_FRANCHISE = 'F';

/** La mention légale d'une entreprise en franchise en base, imprimée sur chaque pièce qu'elle émet. */
export const FRANCHISE_VAT_MENTION = 'TVA non applicable, art. 293 B du CGI';

/**
 * LE CODE TVA D'UN TAUX SOUS UN RÉGIME — la règle unique de la charge signée, des lignes et des blocs persistés.
 * ⚠️ SEUL LE TAUX NUL CHANGE DE CODE EN FRANCHISE : une reprise d'une vente scellée à 20 % avant l'entrée en
 *    franchise rend la TVA collectée alors, à 20 %, sous le code de ce taux.
 */
export function vatCodeFor(rateBp: number, regime: VatRegime): string {
  return regime === 'franchise' && rateBp === 0 ? VAT_CODE_FRANCHISE : defaultVatCode(rateBp);
}

/**
 * POSE SUR CHAQUE BLOC LE CODE DE SON RÉGIME — une ventilation qui va être SCELLÉE porte ses codes.
 * ⚠️ AU RÉGIME NORMAL ELLE NE CHANGE RIEN à la charge signée : `formatVentilation` déduisait déjà ce code du
 *    taux. Elle le rend EXPLICITE, pour que la ligne persistée, le bloc persisté et la charge disent le même.
 */
export function withVatCodes(breakdown: VatBreakdown, regime: VatRegime): VatBreakdown {
  return { ...breakdown, blocks: breakdown.blocks.map((b) => ({ ...b, code: vatCodeFor(b.rateBp, regime) })) };
}

/**
 * POSE SUR CHAQUE BLOC LE CODE DE SES LIGNES — quand une ligne porte un code qui n'est pas celui du régime du jour.
 * ⚠️ C'EST LE CAS D'UNE REPRISE : elle rend la TVA de la vente d'origine, sous le code que cette vente a SCELLÉ (une vente
 *    en franchise en base se reprend en « F » même si le magasin en est sorti depuis — M11). Le code du jour aurait
 *    fait perdre, ou inventer, la mention légale de la franchise.
 * Un bloc dont toutes les lignes portent le même code le reçoit ; des codes différents sous un même taux (une reprise
 * d'avant la sortie de franchise et un article exonéré du jour, dans un même échange) ne tiennent pas dans un bloc unique :
 * le bloc prend alors le code du régime, comme avant.
 */
export function withLineVatCodes(breakdown: VatBreakdown, lines: readonly { rateBp: number; code: string }[], regime: VatRegime): VatBreakdown {
  return {
    ...breakdown,
    blocks: breakdown.blocks.map((b) => {
      const codes = new Set(lines.filter((l) => l.rateBp === b.rateBp).map((l) => l.code));
      return { ...b, code: codes.size === 1 ? [...codes][0]! : vatCodeFor(b.rateBp, regime) };
    }),
  };
}

/** La pièce relève-t-elle de la franchise ? — dit par ses CODES scellés, donc vrai aussi pour un duplicata. */
export function isFranchiseBreakdown(blocks: readonly { code?: string }[]): boolean {
  return blocks.some((b) => b.code === VAT_CODE_FRANCHISE);
}

/**
 * Sérialisation CANONIQUE de la ventilation pour la signature fiscale
 * (CONFORMITE-NF525 §2.3) : `code:taux_en_pb:HT_centimes:TVA_centimes` joints par `;`, taux croissants.
 * Format FIGÉ — ne jamais réordonner ni reformater (casserait la vérification). Applicable aux deux sens
 * (même forme `VatBreakdown`) ; côté FISCAL/NF525 c'est TOUJOURS `ventilate` (TTC) qui l'alimente.
 * ⚠️ Le CODE a été ajouté en tête de chaque bloc (sérialisation v3, 2026-07-22) : il porte l'exigence
 * R13 du code TVA, et c'est lui qui rendra une ligne en régime de la marge distinguable d'une ligne
 * au régime normal de même taux.
 */
export function formatVentilation(breakdown: VatBreakdown): string {
  return breakdown.blocks.map(b => `${b.code ?? defaultVatCode(b.rateBp)}:${b.rateBp}:${b.htCents}:${b.vatCents}`).join(';');
}
